/**
 *
 * =============================================================================
 *
 * All logic here is kinda obsolete.
 * Would be nice to have filter fields loaded from the database instead of hardcoded.
 *
 * =============================================================================
 *
 */

import { generateRangeArray } from "@/utilities";
import { CatalogueItemsCountPerPage } from "@/config";
import type { Category } from "@/services/collection/categories";
import { CategoryId } from "@/services/collection/categories";
import { gettext } from "@/services/translation";
import { getThemesTree, type ThemesTreeNode } from "@/services/collectibles/themes";

export enum CatalogueFilterFieldType {
  UserInput,
  Dropdown,
}

type DatabaseFilterParameterValue = string | number | null;

const VinylsFilterYearFrom = 1917;
const VinylsFilterYearTo = new Date().getFullYear();

/** @deprecated Rework needed */
export type CatalogueFilterDropdownOption = {
  label: string | number;
  value: string | number;
};

/**
 * Wraps current catalogue filter data.
 */
export class CatalogueFilter {
  public page = 0;
  public perPage: number = CatalogueItemsCountPerPage;

  private allowedFilterParamsKeys: string[] = [];

  private categorySpecificParams: Map<string, DatabaseFilterParameterValue> = new Map();

  constructor(public category: Category) {}

  public async init(): Promise<void> {
    return getFilterFields(this.category).then((fields) => fields.forEach((filterField) => this.allowedFilterParamsKeys.push(filterField.getName())));
  }

  public createApiParams(): { [key: string]: DatabaseFilterParameterValue } {
    return {
      categoryId: this.category.id,
      page: this.page,
      perPage: this.perPage,
      ...Object.fromEntries(this.categorySpecificParams),
    };
  }

  public setParam(paramName: string, paramValue: DatabaseFilterParameterValue): void {
    if (!this.allowedFilterParamsKeys.includes(paramName)) {
      throw new Error("DEV: Parameter '" + paramName + "' is not allowed in filter for category #" + this.category.id + ".");
    }

    const isValueEmpty = paramValue === "" || paramValue === null;
    const paramAlreadyExists = this.categorySpecificParams.has(paramName);
    if (isValueEmpty && paramAlreadyExists) {
      this.categorySpecificParams.delete(paramName);
    } else {
      this.categorySpecificParams.set(paramName, paramValue);
    }
  }

  public getParam(paramName: string): DatabaseFilterParameterValue {
    return this.categorySpecificParams.get(paramName) ?? null;
  }

  public getParamStringValue(paramName: string): string | undefined {
    const value = this.categorySpecificParams.get(paramName);
    if (value === undefined) {
      return undefined;
    }
    if (typeof value !== "string") {
      throw new Error("DEV: Should not happend.");
    }
    return value;
  }

  public nextPage(): void {
    this.page++;
  }

  public previousPage(): void {
    this.page--;
  }

  public setPage(newCurrentPage: number): void {
    this.page = newCurrentPage;
  }
}

/**
 * Returns filter fields definitions. Useful for filter HTML rendering.
 **/
export async function getFilterFields(category: Category): Promise<CatalogueFilterField[]> {
  if (category.id === CategoryId.VinylRecords) {
    const VinylsLabelName = gettext.$gettext("Název / Cat / Kód");
    const VinylsLabelInterpret = gettext.$gettext("Interpret");
    const VinylsLabelYear = gettext.$gettext("Rok vydání");
    const VinylsLabelCountry = gettext.$gettext("Země");
    const VinylsLabelFormat = gettext.$gettext("Formát");
    return [
      new CatalogueFilterField(CatalogueFilterFieldType.UserInput, VinylsLabelName, "identification").setPlaceholder(VinylsLabelName),
      new CatalogueFilterField(CatalogueFilterFieldType.UserInput, VinylsLabelInterpret, "interpret").setPlaceholder(VinylsLabelInterpret),
      new CatalogueFilterField(CatalogueFilterFieldType.Dropdown, VinylsLabelYear, "year").setOptions(generateRangeArray(VinylsFilterYearFrom, VinylsFilterYearTo).map((year) => ({ label: year, value: year }))).setPlaceholder(VinylsLabelYear), //
      new CatalogueFilterField(CatalogueFilterFieldType.Dropdown, VinylsLabelCountry, "country").setOptions(VinylCountries.map((country) => ({ label: country, value: country }))).setPlaceholder(VinylsLabelCountry), //
      new CatalogueFilterField(CatalogueFilterFieldType.Dropdown, VinylsLabelFormat, "format").setOptions(VinylFormats.map((format) => ({ label: format, value: format }))).setPlaceholder(VinylsLabelFormat), //
    ];
  }

  if (category.id === CategoryId.Watches) {
    const WatchesLabelName = gettext.$gettext("Najít hodinky");
    const WatchesLabelBrand = gettext.$gettext("Značka");
    return [
      new CatalogueFilterField(CatalogueFilterFieldType.UserInput, WatchesLabelName, "identification").setPlaceholder(WatchesLabelName),
      new CatalogueFilterField(CatalogueFilterFieldType.Dropdown, WatchesLabelBrand, "brand").setOptions(WatchesBrands.map((brand) => ({ label: brand, value: brand }))).setPlaceholder(WatchesLabelBrand), //
    ];
  }

  if (category.id === CategoryId.BoardGames) {
    const BoardGamesLabelName = gettext.$gettext("Název");
    const BoardGamesLabelAuthors = gettext.$gettext("Autoři");
    const BoardGamesLabelLanguages = gettext.$gettext("Jazyk");
    return [
      new CatalogueFilterField(CatalogueFilterFieldType.UserInput, BoardGamesLabelName, "identification").setPlaceholder(BoardGamesLabelName), //
      new CatalogueFilterField(CatalogueFilterFieldType.UserInput, BoardGamesLabelAuthors, "developer").setPlaceholder(BoardGamesLabelAuthors),
      new CatalogueFilterField(CatalogueFilterFieldType.Dropdown, BoardGamesLabelLanguages, "Language")
        .setOptions(BoardgamesLanguages.map((lang) => ({ label: lang, value: lang }))) //
        .setPlaceholder(BoardGamesLabelLanguages), //
    ];
  }

  if (category.id === CategoryId.LegoSets) {
    const YearFrom = 1949;
    const YearTo = new Date().getFullYear();
    const yearOptions = generateRangeArray(YearFrom, YearTo).map((year) => ({ label: year, value: year }));

    const LegoSetsLabelName = gettext.$gettext("Název předmětu / ID");
    const LegoSetsLabelTheme = gettext.$gettext("Téma");
    const LegoSetsLabelYear = gettext.$gettext("Rok vydání");

    const themeOptions = await createThemesOptions(CategoryId.LegoSets);

    return [
      new CatalogueFilterField(CatalogueFilterFieldType.UserInput, LegoSetsLabelName, "identification").setPlaceholder(LegoSetsLabelName), //
      new CatalogueFilterField(CatalogueFilterFieldType.Dropdown, LegoSetsLabelTheme, "theme").setPlaceholder(LegoSetsLabelTheme).setOptions(themeOptions),
      new CatalogueFilterField(CatalogueFilterFieldType.Dropdown, LegoSetsLabelYear, "year").setPlaceholder(LegoSetsLabelYear).setOptions(yearOptions),
    ];
  }

  if (category.id === CategoryId.LegoMinifigures) {
    const LegoMinifigsLabelName = gettext.$gettext("Najít figurku");
    return [
      new CatalogueFilterField(CatalogueFilterFieldType.UserInput, LegoMinifigsLabelName, "identification").setPlaceholder(LegoMinifigsLabelName), //
    ];
  }

  if (category.id === CategoryId.Pokemon) {
    const themeOptions = await createThemesOptions(CategoryId.Pokemon);
    const LabelTheme = gettext.$gettext("Edice");
    const LabelName = gettext.$gettext("Najít kartu");
    return [
      new CatalogueFilterField(CatalogueFilterFieldType.UserInput, LabelName, "identification"), //
      new CatalogueFilterField(CatalogueFilterFieldType.Dropdown, LabelTheme, "theme").setPlaceholder(LabelTheme).setOptions(themeOptions),
    ];
  }

  return [];
}

/**
 * Represents single HTML field in catalogue filter.
 * Wraps appereance and functions of the HTML element.
 *
 * @deprecated Rework to simple types instead of classes
 */
export class CatalogueFilterField {
  private placeholder = "";
  private options: CatalogueFilterDropdownOption[];

  constructor(
    private type: CatalogueFilterFieldType,
    private label: string,
    private name: string
  ) {}

  public getType(): CatalogueFilterFieldType {
    return this.type;
  }

  public getLabel(): string {
    return this.label;
  }

  public getName(): string {
    return this.name;
  }

  public setOptions(options: CatalogueFilterDropdownOption[]): this {
    this.options = options;
    return this;
  }

  public getOptions(): CatalogueFilterDropdownOption[] {
    return this.options;
  }

  public setPlaceholder(placeholder: string): this {
    this.placeholder = placeholder;
    return this;
  }

  public getPlaceholder(): string {
    return this.placeholder;
  }

  public isType(type: CatalogueFilterFieldType): boolean {
    return this.getType() === type;
  }
}

const VinylCountries = [
  "Československo", // Keep comments to preserve line breaks (prettier fix).
  "Spojené státy americké (USA)", //
  "Velká Británie", //
  "Evropa", //
  "Česká republika", //
  "Německo", //
  "Francie", //
  "Slovensko", //
  "Velká Británie a Evropa", //
  "Kanada", //
  "Svět", //
  "Nizozemsko", //
  "Spojené státy americké a Evropa", //
  "Spojené státy americké a Kanada", //
  "Velká Británie, Evropa a Spojené státy americké", //
  "Itálie", //
  "Spojené státy americké, Kanada a Evropa", //
  "Švédsko", //
  "Austrálie", //
  "Velká Británie a Spojené státy americké", //
  "Španělsko", //
  "Belgie", //
  "Brazílie", //
  "Japonsko", //
  "Skandinávie", //
  "Velká Británie a Irsko", //
  "Německo, Rakousko a Švýcarsko", //
  "Dánsko", //
  "Německá demokratická republika (NDR)", //
  "Finsko", //
  "Spojené státy americké, Kanada a Velká Británie", //
  "Švýcarsko", //
  "SSSR", //
  "Rusko", //
  "Norsko", //
  "Rakousko", //
  "Benelux", //
  "Portugalsko", //
  "Polsko", //
  "Austrálie a Nový Zéland", //
  "Severní Amerika (včetně Mexika)", //
  "Francie a Benelux", //
  "Hongkong", //
  "Argentina", //
  "Nový Zéland", //
  "Velká Británie a Francie", //
  "Australasie", //
  "Jižní Korea", //
  "Bulharsko", //
  "Chile", //
  "Řecko", //
  "Island", //
  "Irsko", //
  "Kolumbie", //
  "Litva", //
  "Jižní Afrika", //
  "Čína", //
  "Kostarika", //
  "Estonsko", //
  "Německo a Švýcarsko", //
  "Maďarsko", //
  "Mexiko", //
  "Maroko", //
  "Rumunsko", //
  "Singapur", //
  "Slovinsko", //
  "Tchaj-wan", //
  "Velká Británie a Německo", //
  "Velká Británie, Evropa a Japonsko", //
  "Uruguay", //
  "Jugoslávie", //
  "Indie", //
  "Peru", //
  "Ekvádor", //
  "Izrael", //
  "Venezuela", //
  "Turecko", //
  "Jamajka", //
  "Chorvatsko", //
  "Nigérie", //
  "Rhodesie", //
  "Ukrajina", //
  "Andorra", //
  "Česká republika a Slovensko", //
  "Lotyšsko", //
  "Libanon", //
  "Lucembursko", //
  "Severní a Jižní Amerika", //
  "Panama", //
  "Trinidad a Tobago", //
];

const VinylFormats = [
  "LP", // Keep comments to preserve line breaks (prettier fix).
  '7"', //
  "45 RPM", //
  "Album", //
  "Single", //
  '12"', //
  "Stereo", //
  "33 ⅓ RPM", //
  "Compilation", //
  "Promo", //
  "EP", //
  "Limited Edition", //
  "Reissue", //
  "Mono", //
  "White Label", //
  '10"', //
  "Repress", //
  "Unofficial Release", //
  "Test Pressing", //
  "Numbered", //
  "Single Sided", //
  "Maxi-Single", //
  "Remastered", //
  "Box Set", //
  "Club Edition", //
  "Styrene", //
  "Picture Disc", //
  "78 RPM", //
  "CD", //
  "Special Edition", //
  "Mini-Album", //
  "All Media", //
  "Misprint", //
  "Jukebox", //
  "Sampler", //
  "Deluxe Edition", //
  "Transcription", //
  "Record Store Day", //
  "Etched", //
  "Mixed", //
  "Quadraphonic", //
  "Mispress", //
  "Partially Mixed", //
  "Shape", //
  '8"', //
  "Advance", //
  "DVD", //
];

const BoardgamesLanguages = [
  "čeština", //
  "angličtina", //
  "slovenština", //
  "afrikánština", //
  "albánština", //
  "arabština", //
  "arménština", //
  "barmština", //
  "baskičtina", //
  "bosenština", //
  "bretonština", //
  "bulharština", //
  "běloruština", //
  "cherokee", //
  "chorvatština", //
  "dánština", //
  "esperanto", //
  "estonština", //
  "faerština", //
  "filipínština", //
  "finština", //
  "francouzština", //
  "galicijština", //
  "gruzínština", //
  "hebrejština", //
  "hindština", //
  "holandština", //
  "indonéština", //
  "irština", //
  "islandština", //
  "italština", //
  "japonština", //
  "katalánština", //
  "korejština", //
  "latinština", //
  "litevština", //
  "lotyšština", //
  "lucemburština", //
  "makedonština", //
  "malajština", //
  "maltština", //
  "mapudungun", //
  "maďarština", //
  "norština", //
  "němčina", //
  "perština", //
  "polština", //
  "portugalština", //
  "rumunština", //
  "ruština", //
  "rétorománština", //
  "sališské jazyky", //
  "sardinština", //
  "skotská gaelština", //
  "slovinština", //
  "srbština", //
  "tamilština", //
  "thajština", //
  "turečtina", //
  "ukrajinština", //
  "velština", //
  "vietnamština", //
  "znakové jazyky", //
  "ázerbájdžánština", //
  "čínština", //
  "řečtina", //
  "španělština", //
  "švédština", //
];

const WatchesBrands = ["A. Lange &amp; Söhne", "Alexandre Meerson", "Amida", "Anonimo", "Äonic", "Apple", "Archimede", "Armand Nicolet", "Armin Strom", "Arnold &amp; Son", "Audemars Piguet", "Avoirdupois", "Ball Watch", "Bastian Antoni", "Baume & Mercier", "Bausele", "Bell & Ross", "Bell &amp; Ross", "Bereve", "Berneron", "Blancpain", "Breguet", "Breitling", "Bremont", "Breva", "Bulgari", "Carl F. Bucherer", "Cartier", "Casio", "Certina", "Chopard", "Christiaan van der Klaauw", "Christopher Ward", "Citizen", "CODE41", "Corum", "Crafter Blue", "Cuervo y Sobrinos", "Czapek", "D. Dornblueth & Sohn", "D. Malchert", "Damasko", "Dan Henry", "Daniel Roth", "De Bethune", "Defakto", "Dietrich", "Doxa", "Eberhard", "Elka Watch Co.", "ETERNA", "Excelsior Park", "Fiona Krüger", "Formex", "Fortis", "FP Journe", "Franck Muller", "Frederique Constant", "Furlan Marri", "Gerald Charles", "Girard-Perregaux", "Giuliano Mazzuoli", "Glashütte Original", "Glycine", "Graham", "Grand Seiko", "Grönefeld", "H. Moser & Cie", "H. Moser &amp; Cie", "Hamilton", "Harry Winston", "Hautlence", "Hermès", "Hublot", "Ikepod", "Itay Noy", "IWC", "Jaeger-LeCoultre", "Jaquet Droz", "JeanRichard", "Junghans", "Karoshi", "Kikuchi Nakagawa", "Kobold", "Kollokium", "L & Jr", "Laarvee", "Laco", "Lang & Heyne", "Lang 1943", "Lebois & Co", "Leijona Heritage", "Leroy", "Ligure", "Limes", "Linde Werdelin", "Longines", "Louis Erard", "Louis Vuitton", "Lundis Bleus", "Lyrique", "Lytt Labs", "Massena LAB", "MB&amp;F", "MB&F", "MeisterSinger", "Mido", "Milus", "Minase", "Montblanc", "Montegrappa", "Moritz Grossmann", "Mühle Glashütte", "MW&Co", "Nivada Grenchen", "Nomos Glashütte", "Norqain", "Ollech & Wajs", "Omega", "Ophion", "Oris", "Panerai", "Parmigiani Fleurier", "Patek Philippe", "Pequignet", "Perrelet", "Pheidippides Watches", "Piaget", "Pinion", "Porsche Design", "Prim", "Rado", "Ralph Lauren", "Reservoir", "Ressence", "RGM", "Richard Mille", "Robot Watch", "Roger Dubuis", "Rolex", "Schwarz Etienne", "Seiko", "SevenFriday", "Sherpa", "Sinn", "Speake-Marin", "Staudt", "Steinhart", "Stowa", "Stuckx", "Swatch", "SYE", "TAG Heuer", "Takano", "Tissot", "Toledano &amp; Chan", "Tudor", "TWCO ", "Ulysse Nardin", "Unimatic", "Universal Geneve", "Urwerk", "Vacheron Constantin", "Van der Gang Watches", "Victorinox", "Wempe Glashütte I/SA", "Wyler Geneve", "Zeitwinkel", "Zenith"];

export async function createThemesOptions(categoryId: CategoryId): Promise<CatalogueFilterDropdownOption[]> {
  return getThemesTree(categoryId).then((tree) => tree.map((node) => buildThemesOptions(node)).flat());
}

const buildThemesOptions = (themeNode: ThemesTreeNode, previousThemeNames: string[] = []): CatalogueFilterDropdownOption[] => {
  const Delimiter = " » ";

  const name = themeNode.theme.name;
  const currentNamesPath = [...previousThemeNames, name];
  const label = currentNamesPath.join(Delimiter);
  const value = themeNode.theme.id;

  const nodes = [];
  nodes.push({ label, value });

  for (const subtheme of themeNode.subthemes) {
    nodes.push(...buildThemesOptions(subtheme, currentNamesPath));
  }

  return nodes;
};
